import { useEffect, useState } from "react";
import {
  ArrowLeftIcon,
  CalendarBlankIcon,
  ClockIcon,
  EyeIcon,
  FlagIcon,
  HeartIcon,
  MapPinIcon,
  PaperPlaneTiltIcon,
  PhoneIcon,
  ShareNetworkIcon,
  ShieldCheckIcon,
  TShirtIcon,
  UserIcon,
  WhatsappLogoIcon,
} from "@phosphor-icons/react";
import { api } from "./lib/api.js";
import { fechaLegible, horaLegible, numeroContacto } from "./lib/detalleReporte.js";
import { MapaUbicacion } from "./UbicacionMascotaForm.jsx";
import { NavegacionDetalle } from "./DetalleMascota.jsx";
import HistoriaDesdePerfil from "./HistoriaDesdePerfil.jsx";
import ReportarAnuncioModal from "./ReportarAnuncioModal.jsx";

const etiquetas = {
  HOMBRE: "Hombre", MUJER: "Mujer", NO_ESPECIFICA: "No especifica",
  DELGADA: "Delgada", MEDIANA: "Mediana", ROBUSTA: "Robusta",
  BLANCA: "Clara", TRIGUENA: "Trigueña", OSCURA: "Oscura",
  NEGRO: "Negro", CASTANO_OSCURO: "Castaño oscuro", CASTANO: "Castaño", CASTANO_CLARO: "Castaño claro",
  RUBIO: "Rubio", RUBIO_OSCURO: "Rubio oscuro", PELIRROJO: "Pelirrojo", CANOSO: "Canoso", TENIDO: "Teñido", CALVO: "Calvo",
  MARRON_OSCURO: "Marrón oscuro", MARRON: "Marrón", MIEL: "Miel", VERDE: "Verde", AZUL: "Azul", GRIS: "Gris",
  HETEROCROMIA: "Dos colores diferentes",
};

function texto(valor, alternativo = "Sin especificar") {
  return etiquetas[valor] ?? alternativo;
}

function ubicacionLegible(reporte) {
  return [reporte.ciudad, reporte.departamento].filter(Boolean).join(", ") || "Ubicación registrada en el mapa";
}

export default function DetallePersona({ id, onBack, onNavigate }) {
  const [reporte, setReporte] = useState(null);
  const [estado, setEstado] = useState("cargando");
  const [error, setError] = useState("");
  const [fotoActiva, setFotoActiva] = useState(0);
  const [favorito, setFavorito] = useState(() => localStorage.getItem(`encuentrayayuda:favorito-persona:${id}`) === "1");
  /// Confirmacion de 'compartir': sin esto la accion no daba ninguna senal
  /// y no se sabia si habia pasado algo.
  const [avisoCompartir, setAvisoCompartir] = useState('');
  const [verMas, setVerMas] = useState(false);
  const [mostrarHistoria, setMostrarHistoria] = useState(false);
  const [mostrarDenuncia, setMostrarDenuncia] = useState(false);

  useEffect(() => {
    let vigente = true;
    setEstado("cargando");
    setFotoActiva(0);
    api.personas.detalle(id)
      .then((datos) => { if (vigente) { setReporte(datos); setEstado("listo"); } })
      .catch((err) => { if (vigente) { setError(err.message); setEstado("error"); } });
    return () => { vigente = false; };
  }, [id]);

  const fotos = reporte?.multimedia?.filter((medio) => medio.tipo === "FOTO") ?? [];
  const desaparecida = reporte?.tipo !== "ENCONTRADO";
  const evento = desaparecida ? reporte?.ultimaVistaEn : reporte?.encontradaEn;
  const punto = desaparecida ? reporte?.ubicacion?.perdido : reporte?.ubicacion?.encontro;
  const whatsapp = reporte?.contacto?.whatsapp ?? "";
  const telefono = numeroContacto(whatsapp);

  function alternarFavorito() {
    const siguiente = !favorito;
    setFavorito(siguiente);
    if (siguiente) localStorage.setItem(`encuentrayayuda:favorito-persona:${id}`, "1");
    else localStorage.removeItem(`encuentrayayuda:favorito-persona:${id}`);
  }

  async function compartir() {
    const datos = { title: `Ayuda a encontrar a ${reporte?.nombrePersona || "esta persona"}`, text: reporte?.senalesParticulares || "Comparte este reporte.", url: window.location.href };
    // navigator.share ya muestra la hoja del sistema: ahi el usuario ve el
    // resultado. El respaldo de portapapeles es silencioso, y ese es el caso
    // que necesita confirmacion explicita.
    try {
      if (navigator.share) {
        await navigator.share(datos);
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      mostrarAviso("Enlace copiado");
    } catch (error) {
      // AbortError = el usuario cerro la hoja de compartir a proposito.
      if (error?.name === "AbortError") return;
      mostrarAviso("No se pudo compartir. Copia la dirección de la barra.");
    }
  }

  function mostrarAviso(texto) {
    setAvisoCompartir(texto);
    setTimeout(() => setAvisoCompartir(""), 2600);
  }

  if (estado !== "listo") {
    return <main className="pet-detail-state"><button type="button" onClick={onBack}><ArrowLeftIcon weight="bold" />Volver</button>{estado === "cargando" ? <><span className="loader" /><p>Cargando el perfil…</p></> : <><UserIcon weight="duotone" /><h1>No pudimos cargar el perfil</h1><p>{error}</p></>}</main>;
  }

  const nombre = reporte.nombrePersona || "Persona sin identificar";
  const cabello = reporte.coloresCabello?.map((color) => texto(color)).join(", ") || texto(reporte.colorCabello);
  const detallesAdicionales = [reporte.senalesParticulares, reporte.vestimenta, reporte.informacionAdicional].filter(Boolean);

  return <div className="pet-detail-page person-detail-page">
    <header className="pet-detail-header"><button type="button" onClick={onBack} aria-label="Volver"><ArrowLeftIcon weight="bold" /></button><h1>Detalle de la persona</h1><div><button type="button" onClick={compartir} aria-label="Compartir reporte"><ShareNetworkIcon weight="bold" /></button><button type="button" className={favorito ? "is-favorite" : ""} onClick={alternarFavorito} aria-label={favorito ? "Quitar de favoritos" : "Guardar en favoritos"}><HeartIcon weight={favorito ? "fill" : "bold"} /></button></div></header>
      {avisoCompartir && <p className="aviso-compartir" role="status">{avisoCompartir}</p>}

    <main className="pet-detail-content">
      <div className="pet-detail-meta"><span><UserIcon weight="fill" />Persona {desaparecida ? "desaparecida" : "encontrada"}</span><time dateTime={reporte.createdAt}><CalendarBlankIcon />Reportado el {fechaLegible(reporte.createdAt)}</time></div>

      <section className="person-detail-hero">
        <div className="pet-gallery person-detail-gallery" aria-label={`Fotos de ${nombre}`}><div className="pet-gallery__main">{fotos[fotoActiva] ? <><img className="pet-gallery__backdrop" src={fotos[fotoActiva].url} alt="" aria-hidden="true" /><img className="pet-gallery__photo" src={fotos[fotoActiva].url} alt={`Fotografía ${fotoActiva + 1} de ${nombre}`} /></> : <UserIcon weight="fill" />}<span>{fotos.length ? `${fotoActiva + 1}/${fotos.length}` : "Sin fotos"}</span></div>{fotos.length > 1 && <div className="pet-gallery__thumbs">{fotos.map((foto, indice) => <button type="button" className={fotoActiva === indice ? "is-active" : ""} onClick={() => setFotoActiva(indice)} aria-label={`Ver fotografía ${indice + 1}`} key={foto.id}><img src={foto.urlThumb || foto.url} alt="" /></button>)}</div>}</div>
        <aside className="pet-detail-card person-detail-identity"><h2>{nombre}</h2><dl><div><dt><UserIcon /></dt><dd>{reporte.edadAproximada != null ? `${reporte.edadAproximada} años` : "Edad sin especificar"}</dd></div><div><dt><UserIcon /></dt><dd>{texto(reporte.sexo)}</dd></div><div><dt><UserIcon /></dt><dd>{reporte.estaturaAproximada != null ? `${Number(reporte.estaturaAproximada).toFixed(2)} m` : "Estatura sin especificar"}</dd></div><div><dt><MapPinIcon /></dt><dd>{ubicacionLegible(reporte)}</dd></div></dl></aside>
      </section>

      <section className="pet-detail-card pet-detail-location"><div><MapPinIcon weight="fill" /><div><strong>{desaparecida ? "Última vez visto" : "Lugar del hallazgo"}</strong><p>{ubicacionLegible(reporte)}</p></div></div>{punto && <div className="pet-detail-map"><MapaUbicacion latitud={punto.lat} longitud={punto.lng} precision={null} onLocation={() => {}} etiqueta="Ubicación aproximada de la persona" /></div>}<div className="pet-detail-date"><span><CalendarBlankIcon weight="bold" /><strong>{fechaLegible(evento)}</strong><small>Fecha</small></span><span><ClockIcon weight="bold" /><strong>{horaLegible(evento)}</strong><small>Hora aproximada</small></span></div></section>

      <section className="pet-detail-card person-detail-physical"><h2><UserIcon />Descripción física</h2><ul><li>Contextura {texto(reporte.contextura).toLowerCase()}</li><li>Tez {texto(reporte.tezPiel).toLowerCase()}</li><li>Cabello {cabello.toLowerCase()}</li><li>Ojos {texto(reporte.colorOjos).toLowerCase()}</li></ul>{detallesAdicionales.length > 0 && <button type="button" onClick={() => setVerMas((valor) => !valor)}>{verMas ? "Ocultar características" : "Ver más características"}</button>}{verMas && <div className="person-detail-more">{reporte.senalesParticulares && <p><EyeIcon /><span><strong>Señas particulares</strong>{reporte.senalesParticulares}</span></p>}{reporte.vestimenta && <p><TShirtIcon /><span><strong>Vestimenta</strong>{reporte.vestimenta}</span></p>}{reporte.informacionAdicional && <p><UserIcon /><span><strong>Información adicional</strong>{reporte.informacionAdicional}</span></p>}</div>}</section>

      <section className="pet-detail-card pet-detail-reporter"><div><small>Reportado por</small><strong><UserIcon weight="duotone" />{reporte.contacto?.nombre || "Usuario de la comunidad"}</strong></div>{whatsapp && <a href={whatsapp} target="_blank" rel="noreferrer" aria-label="Contactar por WhatsApp"><WhatsappLogoIcon weight="fill" /></a>}</section>

      {desaparecida && <button className="story-create-button" type="button" onClick={() => setMostrarHistoria(true)}><PaperPlaneTiltIcon weight="fill" /><span><strong>Crear imagen para historias</strong><small>Genera un afiche vertical listo para compartir</small></span></button>}

      <aside className="pet-detail-trust"><ShieldCheckIcon weight="duotone" /><div><strong>Reporte activo</strong><p>Si tienes información que pueda ayudar a encontrar a {nombre}, contacta a la persona que reportó.</p></div></aside>

      <button className="report-listing-button" type="button" onClick={() => setMostrarDenuncia(true)}><FlagIcon weight="bold" />Reportar este anuncio</button>

      {whatsapp && <div className="pet-detail-actions"><a href={whatsapp} target="_blank" rel="noreferrer"><WhatsappLogoIcon weight="fill" />Contactar por WhatsApp</a>{telefono && <a href={`tel:+${telefono}`}><PhoneIcon weight="fill" />Llamar</a>}</div>}
    </main>

    <NavegacionDetalle onBack={onBack} onNavigate={onNavigate} vistaReportar="persona-desaparecida" />
    {mostrarHistoria && <HistoriaDesdePerfil entidad="persona" reporte={reporte} onClose={() => setMostrarHistoria(false)} />}
    {mostrarDenuncia && <ReportarAnuncioModal entidad="persona" reporteId={reporte.id} onClose={() => setMostrarDenuncia(false)} />}
  </div>;
}
